import { Helmet } from 'react-helmet';

import Body from '@/components/Body';
import Button from '@/components/Button/Button';
import EmptyMessage from '@/components/EmptyMessage/EmptyMessage';
import FAIcon from '@/components/Icon/FAIcon';
import Layout from '@/components/Layout';
import { type Viewer } from '@/core/viewer';
import { sand } from '@/theme/colors';
import { queryPart } from '@/utils';

interface ExpertRequestNotOpenGateProps {
  /** ER name for the document title. */
  name?: string | null;
  /** Search tags forwarded to the sign-up CTA (public landing page only). */
  tags?: string[];
  viewer: Viewer;
}

/**
 * "This request isn't open right now" screen, shown when an ER is draft or closed
 * (ENG-5290 D27 / ENG-5318). On Hold keeps applications open, so it is not gated
 * here. Shared by the public landing page and the application form so a deep link
 * straight into request_add gets the same gate, not a dead-end.
 */
export function ExpertRequestNotOpenGate({
  name,
  tags = [],
  viewer,
}: ExpertRequestNotOpenGateProps): JSX.Element {
  const loggedIn = !!(viewer && viewer.id);
  return (
    <Layout verticalCenter>
      <Helmet>
        <title>{name ?? ''}</title>
      </Helmet>
      <Body>
        <EmptyMessage
          border={false}
          // EmptyMessage (legacy v1) exposes padding only via `style` — no className hook.
          style={{ padding: '30px 0 15px' }}
          icon={<FAIcon icon="archive" color={sand} size={52} />}
          title="This request isn’t open right now."
          body={
            loggedIn
              ? 'Our client is not engaging experts at the moment. Keep your profile up to date so we can match you to new requests.'
              : 'Join OnFrontiers and get matched to expert requests in your field.'
          }
          action={
            loggedIn ? (
              <Button
                size="large"
                href={viewer.username ? `/profile/${viewer.username}` : '/dashboard'}
              >
                {viewer.username ? 'Go to my profile' : 'Go to my dashboard'}
              </Button>
            ) : (
              <Button size="large" href={`/signup/expert${queryPart({ t: tags })}`}>
                SIGN UP AS AN EXPERT
              </Button>
            )
          }
        />
      </Body>
    </Layout>
  );
}
